import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  MonitorSmartphone, Inbox, Building2, Loader2, Check, X, Clock, MonitorUp,
} from 'lucide-react';

const STATUS_META = {
  pending: { icon: Loader2, spin: true, cls: 'border-amber-500/40 text-amber-600', label: 'In attesa…' },
  accepted: { icon: Check, cls: 'border-emerald-500/40 text-emerald-600', label: 'Accettata' },
  declined: { icon: X, cls: 'border-rose-500/40 text-rose-600', label: 'Rifiutata' },
  expired: { icon: Clock, cls: 'border-border text-muted-foreground', label: 'Scaduta' },
};

export default function GestoreRemoteSupport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [sessions, setSessions] = useState({});   // restaurant_id -> request view
  const [loading, setLoading] = useState(true);
  const openViewer = useCallback((sessionId) => { if (sessionId) navigate(`/gestore/assistenza-remota/sessione/${sessionId}`); }, [navigate]);
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  const loadRestaurants = useCallback(async () => {
    try { const { data } = await adminApi.get('/admin/restaurants'); setRestaurants(data || []); }
    catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  const loadIncoming = useCallback(async () => {
    try { const { data } = await adminApi.get('/remote/support/manager/incoming'); setIncoming(Array.isArray(data) ? data : []); }
    catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadRestaurants(); }, [loadRestaurants]);

  // Poll inbox (locale -> gestore) in tempo quasi reale
  useEffect(() => {
    loadIncoming();
    const id = setInterval(loadIncoming, 3000);
    return () => clearInterval(id);
  }, [loadIncoming]);

  // Poll stato delle richieste inviate (gestore -> locale) ancora pending
  useEffect(() => {
    const id = setInterval(async () => {
      const current = sessionsRef.current;
      const pend = Object.values(current).filter((s) => s.status === 'pending');
      for (const s of pend) {
        try {
          const { data } = await adminApi.get(`/remote/support/manager/${s.id}`);
          setSessions((prev) => ({ ...prev, [data.restaurant_id]: data }));
          if (data.status === 'accepted') {
            toast.success(`${data.restaurant_name}: ${t('grs.accepted', { defaultValue: 'accesso accettato' })}`);
            openViewer(data.session_id);
          } else if (data.status === 'declined') toast.error(`${data.restaurant_name}: ${t('grs.declined', { defaultValue: 'accesso rifiutato' })}`);
          else if (data.status === 'expired') toast.warning(`${data.restaurant_name}: ${t('grs.expired', { defaultValue: 'richiesta scaduta' })}`);
        } catch (e) { /* noop */ }
      }
    }, 2500);
    return () => clearInterval(id);
  }, [t, openViewer]);

  const requestControl = async (r) => {
    try {
      const { data } = await adminApi.post('/remote/support/manager/request', { restaurant_id: r.id });
      setSessions((prev) => ({ ...prev, [r.id]: data }));
      toast.info(`${r.name}: ${t('grs.requested', { defaultValue: 'richiesta inviata, in attesa di conferma dal locale…' })}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('grs.reqError', { defaultValue: 'Impossibile inviare la richiesta' }));
    }
  };

  const respondIncoming = async (req, action) => {
    try {
      const { data } = await adminApi.post(`/remote/support/manager/${req.id}/respond`, { action });
      setIncoming((prev) => prev.filter((x) => x.id !== req.id));
      if (action === 'accepted') {
        toast.success(t('grs.youAccepted', { defaultValue: 'Richiesta accettata' }));
        openViewer(data?.session_id);
      } else toast(t('grs.youDeclined', { defaultValue: 'Richiesta rifiutata' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('grs.error', { defaultValue: 'Errore' }));
    }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6" data-testid="gestore-remote-support-page">
      <GestorePageHeader
        title={t('grs.title', { defaultValue: 'Assistenza Remota' })}
        subtitle={t('grs.subtitle', { defaultValue: 'Ricevi richieste dai locali assegnati e avvia sessioni di controllo remoto.' })}
      />

      {/* Inbox richieste dai locali */}
      <Card className="p-5 mb-5" data-testid="grs-inbox">
        <div className="font-semibold flex items-center gap-2 mb-3">
          <Inbox className="h-4 w-4" /> {t('grs.inbox', { defaultValue: 'Richieste dai locali' })}
          {incoming.length > 0 && <Badge className="ml-1">{incoming.length}</Badge>}
        </div>
        {incoming.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="grs-inbox-empty">{t('grs.inboxEmpty', { defaultValue: 'Nessuna richiesta in arrivo.' })}</div>
        ) : (
          <div className="space-y-2">
            {incoming.map((req) => (
              <div key={req.id} className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`grs-incoming-${req.id}`}>
                <div className="min-w-0 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 shrink-0"><Building2 className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{req.restaurant_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{req.requested_by_name}{req.message ? ` · "${req.message}"` : ''}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-600" onClick={() => respondIncoming(req, 'declined')} data-testid={`grs-decline-${req.id}`}><X className="h-4 w-4 mr-1" /> {t('grs.decline', { defaultValue: 'Rifiuta' })}</Button>
                  <Button size="sm" onClick={() => respondIncoming(req, 'accepted')} data-testid={`grs-accept-${req.id}`}><Check className="h-4 w-4 mr-1" /> {t('grs.accept', { defaultValue: 'Accetta' })}</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Locali assegnati: invia richiesta di controllo */}
      <Card className="p-5" data-testid="grs-restaurants">
        <div className="font-semibold flex items-center gap-2 mb-3"><MonitorSmartphone className="h-4 w-4" /> {t('grs.myLocali', { defaultValue: 'I miei locali' })}</div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">{t('common.loading', { defaultValue: 'Caricamento…' })}</div>
        ) : restaurants.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="grs-no-locali">{t('grs.noLocali', { defaultValue: 'Nessun locale assegnato.' })}</div>
        ) : (
          <div className="space-y-2">
            {restaurants.map((r) => {
              const sess = sessions[r.id];
              const meta = sess ? STATUS_META[sess.status] : null;
              const isPending = sess?.status === 'pending';
              return (
                <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`grs-restaurant-${r.id}`}>
                  <div className="min-w-0 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground shrink-0"><Building2 className="h-5 w-5" /></span>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{r.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.address || r.id}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {meta && (
                      <Badge variant="outline" className={cn('gap-1', meta.cls)} data-testid={`grs-status-${r.id}`}>
                        <meta.icon className={cn('h-3.5 w-3.5', meta.spin && 'animate-spin')} />
                        {meta.label}{isPending && sess.expires_in ? ` (${sess.expires_in}s)` : ''}
                      </Badge>
                    )}
                    {sess?.status === 'accepted' && sess.session_id && sess.session_status !== 'ended' ? (
                      <Button size="sm" onClick={() => openViewer(sess.session_id)} data-testid={`grs-open-${r.id}`}><MonitorUp className="h-4 w-4 mr-1" /> {t('grs.openSession', { defaultValue: 'Apri sessione' })}</Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={isPending} onClick={() => requestControl(r)} data-testid={`grs-request-${r.id}`}>
                        {isPending ? t('grs.waiting', { defaultValue: 'In attesa…' }) : t('grs.request', { defaultValue: 'Richiedi controllo' })}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

    </div>
  );
}
